import { useCallback, useState } from "react";

/**
 * Where a therapist has moved the tool dock, remembered on this device.
 *
 * Stored as a fraction of the room the dock has to move in, not as pixels:
 * 0 is flush with the left (or top) margin and 1 with the right (or bottom)
 * one. A dock parked in the bottom-right corner of a wide monitor is then
 * still in the bottom-right corner of a laptop, rather than somewhere off
 * its edge — and there is no stored value that can put it off-screen.
 *
 * One key for every page: it is the same dock on home, on a patient and on a
 * session, and a dock that jumps between pages makes the hand look for it.
 */
export interface DockPosition {
    x: number;
    y: number;
}

export interface Size {
    width: number;
    height: number;
}

/** Space the dock keeps from each edge of the viewport, in pixels. */
export interface DockInsets {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

export const DOCK_POSITION_STORAGE_KEY = "alchely.toolDock.position";
export const DOCK_ORIENTATION_STORAGE_KEY = "alchely.toolDock.orientation";

export type DockOrientation = "horizontal" | "vertical";

/**
 * Where a vertical dock lives until it is moved: the right edge, halfway
 * down — a column of tools where a column belongs, beside the page and clear
 * of the sidebar on the left.
 */
export const VERTICAL_HOME: DockPosition = { x: 1, y: 0.5 };

const clampUnit = (value: number) => Math.min(1, Math.max(0, value));

export function parseDockPosition(raw: string | null): DockPosition | null {
    if (!raw) return null;
    try {
        const parsed: unknown = JSON.parse(raw);
        if (
            typeof parsed === "object" &&
            parsed !== null &&
            typeof (parsed as DockPosition).x === "number" &&
            typeof (parsed as DockPosition).y === "number" &&
            Number.isFinite((parsed as DockPosition).x) &&
            Number.isFinite((parsed as DockPosition).y)
        ) {
            return {
                x: clampUnit((parsed as DockPosition).x),
                y: clampUnit((parsed as DockPosition).y),
            };
        }
    } catch {
        // A value this code did not write. Fall back to the default place.
    }
    return null;
}

/** The room the dock's top-left corner can move across, on each axis. */
function travel(viewport: Size, dock: Size, insets: DockInsets): Size {
    return {
        width: Math.max(0, viewport.width - dock.width - insets.left - insets.right),
        height: Math.max(0, viewport.height - dock.height - insets.top - insets.bottom),
    };
}

/** Pixel offsets for a stored position, always inside the viewport. */
export function placeDock(
    position: DockPosition,
    viewport: Size,
    dock: Size,
    insets: DockInsets,
): { left: number; top: number } {
    const room = travel(viewport, dock, insets);
    return {
        left: insets.left + clampUnit(position.x) * room.width,
        top: insets.top + clampUnit(position.y) * room.height,
    };
}

/** The stored position for a dock whose top-left corner is at (left, top). */
export function positionFromOffsets(
    left: number,
    top: number,
    viewport: Size,
    dock: Size,
    insets: DockInsets,
): DockPosition {
    const room = travel(viewport, dock, insets);
    return {
        x: room.width === 0 ? 0.5 : clampUnit((left - insets.left) / room.width),
        y: room.height === 0 ? 1 : clampUnit((top - insets.top) / room.height),
    };
}

/**
 * The remembered position, or null while the dock sits in its default place.
 *
 * Every storage access is guarded: `localStorage` throws in a private window
 * and where site data is blocked, and a position that cannot be remembered is
 * never a reason to refuse the move itself.
 */
export function useDockPosition(
    key: string = DOCK_POSITION_STORAGE_KEY,
): readonly [
    DockPosition | null,
    (position: DockPosition, options?: { persist?: boolean }) => void,
    () => void,
] {
    const [position, setPosition] = useState<DockPosition | null>(() => {
        try {
            return parseDockPosition(window.localStorage.getItem(key));
        } catch {
            return null;
        }
    });

    const move = useCallback(
        (next: DockPosition, { persist = true }: { persist?: boolean } = {}) => {
            setPosition(next);
            if (!persist) return;
            try {
                window.localStorage.setItem(key, JSON.stringify(next));
            } catch {
                // Nothing to do, and nothing worth telling anyone about.
            }
        },
        [key],
    );

    const reset = useCallback(() => {
        setPosition(null);
        try {
            window.localStorage.removeItem(key);
        } catch {
            // As above.
        }
    }, [key]);

    return [position, move, reset] as const;
}

/**
 * Whether the dock is a row or a column, remembered on this device.
 *
 * Kept apart from the position on purpose: a value written before the dock
 * could turn still parses as a position, and anything unrecognised here reads
 * as the row it always was.
 */
export function useDockOrientation(
    key: string = DOCK_ORIENTATION_STORAGE_KEY,
): readonly [DockOrientation, (orientation: DockOrientation) => void] {
    const [orientation, setOrientationState] = useState<DockOrientation>(() => {
        try {
            return window.localStorage.getItem(key) === "vertical" ? "vertical" : "horizontal";
        } catch {
            return "horizontal";
        }
    });

    const setOrientation = useCallback(
        (next: DockOrientation) => {
            setOrientationState(next);
            try {
                if (next === "vertical") window.localStorage.setItem(key, next);
                else window.localStorage.removeItem(key);
            } catch {
                // As with the position: the dock still turns.
            }
        },
        [key],
    );

    return [orientation, setOrientation] as const;
}
